/**
 * Shared utilities for parsing seed question tag metadata.
 *
 * Used by both useTarotSeedQuestions and useOracleCardsSeedQuestions hooks.
 */

export interface SeedQuestionTagMeta {
  /** Whether this is a primary action (rendered prominently) */
  isPrimary?: boolean
  /** Action identifier (e.g., 'oracle_pull', 'tarot_new_reading') */
  action?: string
}

export interface ParsedSeedQuestion {
  id: string
  question: string
  tagMeta: SeedQuestionTagMeta
}

export interface CategorizedTarotSeeds {
  start: ParsedSeedQuestion | null
  clarify: ParsedSeedQuestion | null
  others: ParsedSeedQuestion[]
}

export const ACTION_TAROT_NEW_READING = 'tarot_new_reading'
export const ACTION_CLARIFIER_PULL = 'tarot_clarifier_pull'

/**
 * Parses raw GQL seed questions and categorizes them into action buckets
 * so consuming components don't need to inspect tags themselves.
 */
export const categorizeTarotSeeds = (
  seedQuestions: ReadonlyArray<{ id: string; question: string; tags: string[] }>,
): CategorizedTarotSeeds => {
  let start: ParsedSeedQuestion | null = null
  let clarify: ParsedSeedQuestion | null = null
  const others: ParsedSeedQuestion[] = []

  seedQuestions.forEach((sq) => {
    const tagMeta = extractTagMeta(sq.tags)
    const parsed: ParsedSeedQuestion = { id: sq.id, question: sq.question, tagMeta }

    if (tagMeta.action === ACTION_TAROT_NEW_READING) {
      start = parsed
    } else if (tagMeta.action === ACTION_CLARIFIER_PULL) {
      clarify = parsed
    } else {
      others.push(parsed)
    }
  })

  return { start, clarify, others }
}

/**
 * Categorizes already-parsed seed questions (with tagMeta) into action buckets.
 * Use this when seeds have already been through extractTagMeta (e.g., from useTarotSeedQuestions).
 */
export const categorizeTarotSeedsFromParsed = (
  seedQuestions: ReadonlyArray<ParsedSeedQuestion>,
): CategorizedTarotSeeds => {
  let start: ParsedSeedQuestion | null = null
  let clarify: ParsedSeedQuestion | null = null
  const others: ParsedSeedQuestion[] = []

  seedQuestions.forEach((sq) => {
    if (sq.tagMeta.action === ACTION_TAROT_NEW_READING) {
      start = sq
    } else if (sq.tagMeta.action === ACTION_CLARIFIER_PULL) {
      clarify = sq
    } else {
      others.push(sq)
    }
  })

  return { start, clarify, others }
}

/**
 * Attempts to parse a JSON string from the tags array.
 * Returns the parsed object if valid, null otherwise.
 */
export const parseTagJson = (tag: string): Record<string, unknown> | null => {
  if (!tag.startsWith('{')) return null
  try {
    return JSON.parse(tag)
  } catch {
    return null
  }
}

/**
 * Extracts structured metadata from a seed question's tags array.
 * Scans for JSON-encoded tags containing `isPrimary` and/or `action`,
 * as well as colon-delimited key:value tags (e.g., 'action:oracle_pull').
 */
export const extractTagMeta = (tags: string[]): SeedQuestionTagMeta => {
  const meta: SeedQuestionTagMeta = {}

  tags.forEach((tag) => {
    const parsed = parseTagJson(tag)
    if (parsed) {
      if (typeof parsed.isPrimary === 'boolean') meta.isPrimary = parsed.isPrimary
      if (typeof parsed.action === 'string') meta.action = parsed.action
      return
    }

    if (tag === 'priority') meta.isPrimary = true
    else if (tag.startsWith('action:')) meta.action = tag.slice('action:'.length)
  })

  return meta
}

export const buildTarotNewReadingMessage = (question: string, salt: string): string => `<Gaia action="tarot_new_reading" text="${question}" salt="${salt}" />`

export const buildTarotPullMessage = (
  question: string,
  options?: { forceAccept?: boolean },
): string => {
  if (options?.forceAccept) {
    return `<Gaia action="tarot_pull" question="${question}" force_accept="true" />`
  }
  return `<Gaia action="tarot_pull" question="${question}" />`
}
